import { inject, injectable } from 'inversify';
import { IImageScrollStrategy, ImageScrollMode, SliceType } from '@/engine/app/modules/images/interfaces.ts';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes.ts';
import type { ImagesService } from '@/engine/app/modules/images/Images.service.ts';
import { TYPES } from '@/engine/ioc';
import { ImageSliderScrollStrategy } from '@/engine/app/modules/images/scrollStrategies/imageSliderScrollStrategy.ts';
import { ImageRotationScrollStrategy } from '@/engine/app/modules/images/scrollStrategies/imageRotationScrollStrategy.ts';
import { ImagePointToPointRotationScrollStrategy } from '@/engine/app/modules/images/scrollStrategies/imagePointToPointRotationScrollStrategy.ts';
import type { PointsSwitcherController } from '@/components/PointsSwitcher/PointsSwitcher.controller.ts';
import { IObjectViewModel } from '@/engine/app/interfaces.ts';

export interface IImageScrollStrategiesFactory {
  createImageScrollStrategy: (sliceType: SliceType) => IImageScrollStrategy;
}

@injectable()
export class ImageScrollStrategiesFactory implements IImageScrollStrategiesFactory {
  private readonly _imagesService: ImagesService;

  private readonly _pointsSwitcherController: PointsSwitcherController;

  public constructor(
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.PointsSwitcherController) pointsSwitcherController: PointsSwitcherController
  ) {
    this._imagesService = imagesService;
    this._pointsSwitcherController = pointsSwitcherController;
  }

  public createImageScrollStrategy(sliceType: SliceType): IImageScrollStrategy {
    const imagesInfo = this._imagesService.getImageSliceOrientationInfo(sliceType);
    const selectedSplineVm = this._pointsSwitcherController.selectedSplineViewModel;
    const mode = this._defineImageScrollMode(selectedSplineVm, imagesInfo.slicesCount);
    switch (mode) {
      case ImageScrollMode.MPR:
        return new ImageSliderScrollStrategy(this._imagesService);
      case ImageScrollMode.AXIS:
        return new ImageRotationScrollStrategy(this._imagesService);
      case ImageScrollMode.POINTS:
        return new ImagePointToPointRotationScrollStrategy(this._pointsSwitcherController);
    }
  }

  private _defineImageScrollMode(selectedSplineVm: IObjectViewModel | undefined, slicesCount: number): ImageScrollMode {
    if (slicesCount > 0) return ImageScrollMode.MPR;

    if (selectedSplineVm) return ImageScrollMode.POINTS;

    if (slicesCount === 0) return ImageScrollMode.AXIS;

    throw new Error('ImageScrollMode is undefined');
  }
}
